import React, { useState, useEffect, useRef } from 'react';
import { Button, getClassNameFactory, getRehydratableName } from '@dcl/ui';
import { ICopyLinkProps, ICopyState } from './types';
import CheckIcon from './CheckIcon';
import { ButtonMessage, TrackingMessage } from './constants';
import { triggerAnalyticsEvent } from './helper/analytics';
import {
  ButtonColor,
  ButtonKind,
  IconPosition,
} from '@dcl/ui/dist/elements/Button';
import { propsToDataAttributes } from '@tr-digital/react-rehydrate';
import './index.css';
import LinkIcon from './LinkIcon';

export const CopyLink: React.FC<ICopyLinkProps> = (props) => {
  const {
    copyLinkButtonName = ButtonMessage.COPYLINK,
    copyUrlButtonName = ButtonMessage.COPY,
    copiedUrlButtonName = ButtonMessage.COPIED,
  } = props;

  const getClassName = getClassNameFactory(CopyLink.displayName);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUrlCopied, setIsUrlCopied] = useState<ICopyState>({
    isSuccessful: false,
    message: '',
  });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const currentUrl = window.location.href.split('?')[0];

  const handleModalToggle = () => {
    setIsModalOpen(!isModalOpen);
    setIsUrlCopied({ isSuccessful: false, message: '' });
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard?.writeText(currentUrl);
      setIsUrlCopied({ isSuccessful: true, message: copiedUrlButtonName });
      triggerAnalyticsEvent(TrackingMessage.COPYLINK);
    } catch {
      setIsUrlCopied({ isSuccessful: false, message: ButtonMessage.ERROR });
    }
  };

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isModalOpen) {
        setIsModalOpen(false);
      }
    };

    const onClick = (e: MouseEvent) => {
      const target = e.target;
      if (
        isModalOpen &&
        wrapperRef.current &&
        !wrapperRef.current.contains(target as Node)
      ) {
        setIsModalOpen(false);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('click', onClick);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('click', onClick);
    };
  }, [isModalOpen]);

  const getButtonText = !isUrlCopied.isSuccessful && !isUrlCopied.message;
  const copyButtonText = getButtonText ? (
    copyUrlButtonName
  ) : (
    <>{isUrlCopied.message}</>
  );

  return (
    <div
      className={getClassName({ descendantName: 'wrapper' })}
      data-rehydratable={getRehydratableName(CopyLink.displayName)}
      {...propsToDataAttributes(props)}
    >
      <div className={getClassName({ descendantName: 'copyButton' })}>
        <Button
          kind={ButtonKind.BUTTON}
          color={ButtonColor.SECONDARY}
          data-testid="modalButton"
          iconPosition={IconPosition.LEFT}
          icon={<LinkIcon />}
          onClick={handleModalToggle}
          contentLocation="left"
        >
          {copyLinkButtonName}
        </Button>
      </div>

      {isModalOpen && (
        <div
          className={getClassName({
            descendantName: 'tip',
            utilities: 'bottom',
          })}
          ref={wrapperRef}
        >
          <div className={getClassName({ descendantName: 'copyText' })}>
            {currentUrl}
          </div>

          <div
            role={ButtonMessage.BUTTONROLE}
            aria-live="polite"
            className={getClassName({ descendantName: 'buttonBlock' })}
          >
            <Button
              kind={ButtonKind.BUTTON}
              color={ButtonColor.PRIMARY}
              onClick={copyToClipboard}
              block
              iconPosition={IconPosition.LEFT}
              icon={!getButtonText && <CheckIcon />}
              data-testid="copyButton"
            >
              {copyButtonText}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

CopyLink.displayName = 'CopyLink';
